import { Api } from './api';
import type { IBankScrapingManager, IDeposit, ILinkedTransaction, IPage } from '../types';
import type { IBankAccountConfig } from '../types/bankAccountConfig.type';
import type { IPendingTransaction } from '../types/pendingTransaction.type';
import type { IWithdrawal } from '../types/withdrawal.type';

import type { 
  IBankAccount, 
  IRegisterBalancePurchase, 
  IRegisterDeposit, 
  IRegisterTransfer, 
  IRegisterWithdrawal 
} from '../types/bankAccount.type';

interface LinkedTransactionsQuery {
  page?: number; 
  size?: number; 
  balance?: number;
}

export class BankAccountApi extends Api {

  static async getAll() {
    return this.get<IBankAccount[]>('/bank-account');
  }

  static async getById(id: number) {
    return this.get<IBankAccount>(`/bank-account/${id}`);
  }
  
  static async add(bankAccount: { bankId: number; name: string; accountNumber: string; userId?: number }) {
    return this.post<IBankAccount>('/bank-account', bankAccount);
  }

  static async update(id: number, bankAccount: { name: string; accountNumber: string }) {
    return this.patch<IBankAccount>(`/bank-account/${id}`, bankAccount);
  }

  static async getDeposits(id: number, page?: number, size?: number, balance?: number) {
    return this.get<IPage<IDeposit>>(`/bank-account/${id}/deposits`, {
      params: { page, size, balance }
    });
  }

  static async getWithdrawals(id: number, page?: number, size?: number, balance?: number) {
    return this.get<IPage<IWithdrawal>>(`/bank-account/${id}/withdrawals`, {
      params: { page, size, balance }
    });
  }

  static async getPendingTransactions(id: number, page?: number, size?: number) {
    return this.get<IPage<IPendingTransaction>>(`/bank-account/${id}/pending-transactions`, {
      params: { page, size }
    });
  }

  static async getLastBankScrapingManager(id: number) {
    return this.get<IBankScrapingManager>(`/bank-account/${id}/last-bank-scraping-manager`);
  }

  public static async getConfigs(id: number) {
    return this.get<IBankAccountConfig[]>(`/bank-account/${id}/configs`);
  }

  public static async getLinkedTransactions(id: number, type: 'deposits' | 'withdrawals', query: LinkedTransactionsQuery) {
    return this.get<ILinkedTransaction[]>(`/bank-account/${id}/${type}/linked`, {
      params: query
    });
  }

  public static async registerDeposit(id: number, deposit: IRegisterDeposit) {
    return this.post<IDeposit>(`/bank-account/${id}/register-deposit`, deposit);
  }

  public static async registerWithdrawal(id: number, withdrawal: IRegisterWithdrawal) {
    return this.post<IDeposit>(`/bank-account/${id}/register-withdrawal`, withdrawal);
  }

  public static async registerProfitWithdrawal(id: number, withdrawal: IRegisterWithdrawal) {
    return this.post<IDeposit>(`/bank-account/${id}/register-profit-withdrawal`, withdrawal);
  }

  public static async registerTransfer(id: number, transfer: IRegisterTransfer) {
    return this.post<IDeposit>(`/bank-account/${id}/register-transfer`, transfer);
  }

  public static async registerBalancePurchase(id: number, transfer: IRegisterBalancePurchase) {
    return this.post<IDeposit>(`/bank-account/${id}/register-balance-purchase`, transfer);
  }

  public static async registerPendingDeposit(id: number, pendingTransactionId: number, businessId?: number, description?: string) {
    await this.post(`/bank-account/${id}/pending-transactions/${pendingTransactionId}/register-deposit`, {
      businessId,
      description
    });
  }

  public static async registerPendingWithdrawal(id: number, pendingTransactionId: number, description?: string) {
    await this.post(`/bank-account/${id}/pending-transactions/${pendingTransactionId}/register-withdrawal`, {
      description
    });
  }

  public static async registerPendingProfitWithdrawal(id: number, pendingTransactionId: number, description?: string) {
    await this.post(`/bank-account/${id}/pending-transactions/${pendingTransactionId}/register-profit-withdrawal`, {
      description
    });
  }

  public static async linkPendingDeposit(id: number, pendingTransactionId: number, depositId?: number) {
    await this.post(`/bank-account/${id}/pending-transactions/${pendingTransactionId}/link-deposit`, {
      depositId
    });
  }

  public static async linkPendingWithdrawal(id: number, pendingTransactionId: number, withdrawalId?: number) {
    await this.post(`/bank-account/${id}/pending-transactions/${pendingTransactionId}/link-withdrawal`, {
      withdrawalId
    });
  }

  public static async addConfig(id: number, key: string, value: string) {
    return this.post<IBankAccountConfig>(`/bank-account/${id}/configs`, { key, value });
  }

  public static async updateConfig(id: number, configId: number, key: string, value: string) {
    return this.patch<void>(`/bank-account/${id}/configs/${configId}`, { key, value });
  }

  public static async startScrapingService(id: number) {
    await this.post<void>(`/bank-account/${id}/start-scraping-service`);
  }
  
  public static async deleteConfig(configId: number) {
    return this.remove<void>(`/bank-account/configs/${configId}`);
  }
}
